import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Lock, CheckCircle2 } from "lucide-react";
import ContractPages from "@/components/contracts/ContractPages";
import SignatureModal from "@/components/contracts/SignatureModal";
import SignPanel from "@/components/contracts/SignPanel";
import SignersPanel from "@/components/contracts/SignersPanel";
import FieldToolbar from "@/components/contracts/FieldToolbar";
import ContractActions from "@/components/contracts/ContractActions";
import { StatusBadge, FIELD_LABEL, FIELD_SIZE, todayLocal } from "@/components/contracts/contractUtils";

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
const initialsOf = (name) => (name || "").split(/\s+/).filter(Boolean).map((p) => p[0].toUpperCase()).join("").slice(0, 3);

export default function ContractView() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [fields, setFields] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [draft, setDraft] = useState({ type: "signature", signer: "host" });
  const [values, setValues] = useState({});
  const [sigUrl, setSigUrl] = useState(null);
  const [iniUrl, setIniUrl] = useState(null);
  const [modal, setModal] = useState(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const apply = (data) => { setC(data); setFields(data.fields); setDirty(false); };
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/contracts/${id}`); apply(data); }
    catch (e) { setError(e.response?.status === 404 ? "notfound" : formatApiError(e.response?.data?.detail)); }
  }, [id]);
  useEffect(() => { if (user) load(); }, [user, load]);

  const role = c?.status === "sent" ? c.my_pending_roles?.[0] : null;
  useEffect(() => {
    if (!c || !role) return;
    const name = c.signers[role]?.name || "";
    setValues((v) => {
      const n = { ...v };
      c.fields.forEach((f) => { if (f.signer === role && f.type === "name" && !n[f.id]) n[f.id] = name; });
      return n;
    });
  }, [c, role]);

  if (loading) return <div className="p-12 text-[var(--ff-text-dim)] font-mono-ui text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (error) {
    return (
      <div className="max-w-xl mx-auto px-6 py-32 text-center" data-testid="contract-not-found">
        <Lock size={32} className="mx-auto text-[var(--ff-red)] mb-4" />
        <div className="font-display text-3xl mb-2">CONFIDENTIAL.</div>
        <p className="text-[var(--ff-text-dim)] text-sm">{error === "notfound" ? "This contract doesn't exist or you don't have access to it." : error}</p>
      </div>
    );
  }
  if (!c) return <div className="p-12 text-[var(--ff-text-dim)] font-mono-ui text-sm">Loading contract…</div>;

  const lockedRoles = new Set(Object.entries(c.signers).filter(([, s]) => s.signed_at).map(([r]) => r));
  const mode = editing ? "edit" : role ? "sign" : "view";
  const call = async (fn, msg) => {
    setBusy(true);
    try {
      const { data } = await fn();
      if (data?.id) apply(data);
      if (msg) toast.success(msg);
      return true;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; } finally { setBusy(false); }
  };

  const onMove = (fid, patch) => { setFields((fs) => fs.map((f) => (f.id === fid ? { ...f, ...patch } : f))); setDirty(true); };
  const onPlace = (page, x, y) => {
    const [w, h] = FIELD_SIZE[draft.type];
    const pg = c.pages[page];
    const hit = (fx, fy) => fields.some((o) => o.page === page && fx < o.x + o.w && fx + w > o.x && fy < o.y + o.h && fy + h > o.y);
    let fx = clamp(x - w / 2, 0, pg.w - w);
    let fy = clamp(y - h / 2, 0, pg.h - h);
    for (let i = 0; i < 12 && hit(fx, fy) && fy + h + 6 <= pg.h; i += 1) fy += h + 6;
    const f = { id: `new-${Date.now().toString(36)}`, type: draft.type, signer: draft.signer, page, w, h,
      x: fx, y: fy, label: FIELD_LABEL[draft.type],
      required: !["text", "checkbox"].includes(draft.type), value: null };
    setFields((fs) => [...fs, f]);
    setSelectedId(f.id);
    setPlacing(false);
    setDirty(true);
  };
  const selected = fields.find((f) => f.id === selectedId);
  const saveFields = () => call(() => api.patch(`/contracts/${id}`, {
    fields: fields.filter((f) => !lockedRoles.has(f.signer)).map(({ value, filled_at, ...f }) => ({ ...f, id: f.id.startsWith("new-") ? null : f.id })),
  }), "Fields saved");
  const cancelEdit = () => { setFields(c.fields); setEditing(false); setPlacing(false); setSelectedId(null); setDirty(false); };
  const submit = () => {
    const vals = { ...values };
    c.fields.forEach((f) => { if (f.signer === role && f.type === "date") vals[f.id] = todayLocal(); });
    return call(() => api.post(`/contracts/${id}/sign`, { role, values: vals, signature: sigUrl, initials: iniUrl, consent }), "Signed — thank you!");
  };
  const sign = {
    mine: (f) => f.signer === role, values, today: todayLocal(), sigUrl, iniUrl, openModal: setModal,
    setValue: (fid, v) => setValues((s) => ({ ...s, [fid]: v })),
  };
  const ctx = { mode, selectedId, lockedRoles, placing, onPlace, onMove, onSelect: setSelectedId, sign };
  const signerName = role ? c.signers[role]?.name : "";

  return (
    <div className="max-w-[1500px] mx-auto px-4 lg:px-10 py-24" data-testid="contract-view">
      <Link to={c.can_manage ? "/dashboard" : "/contracts"} className="inline-flex items-center gap-1 text-xs font-mono-ui uppercase tracking-[0.15em] text-[var(--ff-text-dim)] hover:text-white mb-6" data-testid="contract-back-link">
        <ArrowLeft size={12} /> {c.can_manage ? "Dashboard" : "My Contracts"}
      </Link>
      <header className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow text-[var(--ff-red)] mb-2 flex items-center gap-1.5"><Lock size={11} /> ▮ CONFIDENTIAL CONTRACT</div>
          <h1 className="font-display text-3xl lg:text-5xl leading-none" data-testid="contract-title">{c.title}</h1>
          <p className="text-[var(--ff-text-dim)] text-xs mt-2">Visible only to its signers, Executive Managers and the Founder.</p>
        </div>
        <StatusBadge status={c.status} testId="contract-status" />
      </header>

      {c.status === "completed" && !c.can_manage && (
        <div className="border border-emerald-400/40 bg-emerald-400/5 p-4 mb-6 text-sm flex items-center gap-2" data-testid="contract-completed-note">
          <CheckCircle2 size={16} className="text-emerald-400" /> Fully signed. Management keeps the signed PDF on file.
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_340px] gap-8 items-start">
        <ContractPages contract={c} fields={fields} ctx={ctx} />
        <aside className="space-y-4 lg:sticky lg:top-20" data-testid="contract-sidebar">
          {mode === "sign" && <SignPanel role={role} fields={c.fields} sign={sign} consent={consent} setConsent={setConsent} onSubmit={submit} busy={busy} />}
          {editing && (
            <FieldToolbar placing={placing} setPlacing={setPlacing} draft={draft} setDraft={setDraft} selected={selected} lockedRoles={lockedRoles}
              onChange={(patch) => onMove(selectedId, patch)} onDelete={() => { setFields((fs) => fs.filter((f) => f.id !== selectedId)); setSelectedId(null); setDirty(true); }}
              onSave={saveFields} onReset={() => window.confirm("Replace unsigned fields with the auto-detected ones?") && call(() => api.patch(`/contracts/${id}`, { reset_detected: true }), "Reset to auto-detected fields")}
              onCancel={cancelEdit} dirty={dirty} count={fields.length} />
          )}
          {c.can_manage && (
            <>
              <SignersPanel contract={c} editable={!["completed", "voided"].includes(c.status)} onSave={(body) => call(() => api.put(`/contracts/${id}/signers`, body), "Signers updated")} />
              <ContractActions c={c} editing={editing} onEdit={() => setEditing(true)}
                onSend={() => call(() => api.post(`/contracts/${id}/send`), c.status === "draft" ? "Sent for signature" : "Discord notices re-sent")}
                onVoid={() => window.confirm("Void this contract? Signers can no longer sign it.") && call(() => api.post(`/contracts/${id}/void`), "Contract voided")}
                onDelete={async () => { if (!window.confirm("Delete this contract permanently?")) return; if (await call(() => api.delete(`/contracts/${id}`), "Deleted")) nav("/dashboard"); }} />
            </>
          )}
          {!c.can_manage && mode === "view" && (
            <div className="surface-card p-5 text-sm text-[var(--ff-text-dim)]" data-testid="contract-view-note">
              {c.status === "sent" ? "You've signed. Waiting on the other parties." : "Read-only view."}
            </div>
          )}
        </aside>
      </div>

      {modal && (
        <SignatureModal kind={modal} defaultText={modal === "initials" ? initialsOf(signerName) : signerName} onClose={() => setModal(null)}
          onAdopt={(url) => { modal === "initials" ? setIniUrl(url) : setSigUrl(url); setModal(null); }} />
      )}
    </div>
  );
}
